import {useCart, useCartLine} from '@shopify/hydrogen';
import {
  AttributeInput,
  CartLineInput,
  CartLineUpdateInput,
  SelectedOption,
} from '@shopify/hydrogen/storefront-api-types';
import {Fragment, useState} from 'react';
import {X} from 'react-feather';

import {Button, useDrawerContext} from '../index';
import {
  ProductBookingModal,
  ProductBookingModalConfirmation,
} from '../booking_modal/ProductBookingModal.client';
import {useMutation} from 'react-query';
import {useCartFetch} from '@shopify/hydrogen/components/CartProvider/hooks.client';
import {gql} from '@shopify/hydrogen';
import {formatNumber} from '~/utils';

export function CartLineItemDetail({handleClose}: {handleClose: () => void}) {
  const {lines, id: cartId, linesUpdate} = useCart();
  const fetchCart = useCartFetch();
  const line = useCartLine();

  const [quantity, setQuantity] = useState(line.quantity);
  const [selectedOptions, setSelectedOptions] = useState<SelectedOption[]>(
    line.merchandise.selectedOptions,
  );
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [bookingModalConfirmation, setBookingModalConfirmation] =
    useState<ProductBookingModalConfirmation>();

  const {setIsOpenMenu} = useDrawerContext();

  const isGift =
    line.attributes.find((e) => e.key === '_tfr_is_gift')?.value === 'true';

  const currentSurgeFee = lines.find(
    (l) =>
      line.merchandise.id ==
      l.attributes.find((a) => a.key === '_for_tfr_line_id')?.value,
  );
  const updatedSurgeFee = bookingModalConfirmation?.feeLine;

  const surgeFeeToDisplay = bookingModalConfirmation
    ? {
        cost: bookingModalConfirmation.timeslot.surgeFee?.amount,
        currencyCode: bookingModalConfirmation?.timeslot.surgeFee?.currencyCode,
      }
    : {
        cost: currentSurgeFee?.cost.totalAmount.amount,
        currencyCode: currentSurgeFee?.cost.totalAmount.currencyCode,
      };

  const {
    mutateAsync: performUpdateCartMutation,
    isLoading: isUpdatingCart,
    isError: isUpdateCartError,
  } = useMutation({
    mutationFn: ({
      linesToUpdate,
      linesToAdd,
      linesToRemove,
    }: {
      linesToUpdate?: CartLineUpdateInput[];
      linesToAdd?: CartLineInput[];
      linesToRemove?: string[];
    }) =>
      fetchCart({
        query: gql`
          mutation updateCart(
            $cartId: ID!
            $linesToUpdate: [CartLineUpdateInput!]!
            $linesToAdd: [CartLineInput!]!
            $linesToRemove: [ID!]!
          ) {
            cartLinesUpdate(cartId: $cartId, lines: $linesToUpdate) {
              cart {
                id
              }
              userErrors {
                field
                message
              }
            }
            cartLinesAdd(cartId: $cartId, lines: $linesToAdd) {
              cart {
                id
              }
              userErrors {
                field
                message
              }
            }
            cartLinesRemove(cartId: $cartId, lineIds: $linesToRemove) {
              cart {
                id
              }
              userErrors {
                field
                message
              }
            }
          }
        `,
        variables: {
          cartId,
          linesToUpdate,
          linesToAdd,
          linesToRemove,
        },
      }).then(({errors, data}) => {
        if (errors) {
          throw new Error(errors.map((e: any) => e.message).join(', '));
        }
        return data;
      }),
  });

  const handleUpdate = async () => {
    const toUpdate = {
      ...line,
      quantity,
      merchandise: {
        ...line.merchandise,
        selectedOptions: [
          ...selectedOptions.map((option) => {
            if (option.name.includes('Add-On')) {
              return {name: option.name, value: option.name};
            } else {
              return option;
            }
          }),
        ],
      },
      attributes: bookingModalConfirmation?.productLine.attributes,
    };

    const lineUpdateInput: CartLineUpdateInput = {
      id: toUpdate.id,
      attributes: toUpdate.attributes as Array<AttributeInput>,
      merchandiseId: toUpdate.merchandise.id,
      quantity: toUpdate.quantity,
    };

    const linesToUpdate = [
      lineUpdateInput,
      //If theres already a surge fee, update it if there's a replacement
      ...(bookingModalConfirmation && currentSurgeFee && updatedSurgeFee
        ? [
            {
              id: currentSurgeFee.id,
              merchandiseId: updatedSurgeFee.merchandiseId,
              attributes: updatedSurgeFee.attributes,
            },
          ]
        : []),
    ];
    //If we changed the date, there's a fee now, and there wasnt before, add it
    const linesToAdd =
      bookingModalConfirmation && updatedSurgeFee && !currentSurgeFee
        ? [
            {
              merchandiseId: updatedSurgeFee.merchandiseId,
              attributes: updatedSurgeFee.attributes,
            },
          ]
        : [];

    //If we changed the date and there's no fee now, and there was before, remove it
    const linesToRemove =
      bookingModalConfirmation && !updatedSurgeFee && currentSurgeFee
        ? [currentSurgeFee.id]
        : [];

    await performUpdateCartMutation({linesToUpdate, linesToAdd, linesToRemove});

    //Need to call this to force useCart hook data used by other components to refresh
    linesUpdate([]);

    handleClose();
  };

  const handleSelectOption = (option: SelectedOption) => {
    const options = selectedOptions.map((e) =>
      e.name === option.name ? option : e,
    );
    setSelectedOptions(options);
  };

  const handleRemoveOption = (option: SelectedOption) => {
    const options = selectedOptions.map((e) =>
      e.name === option.name ? option : e,
    );
    setSelectedOptions(options);
  };

  // const {options} = useProductOptions();

  return (
    <form className="flex h-full flex-col text-acai-900">
      <ProductBookingModal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
        }}
        isGift={isGift}
        confirmButtonTitle="Update Selection"
        onConfirmTimeslot={setBookingModalConfirmation}
      />
      <div className="flex items-center justify-between">
        <div className="text-xl">{line.merchandise.product.title}</div>
        <button type="button" onClick={handleClose}>
          <X width={24} height={24} className="" />
        </button>
      </div>
      <div className="pb-8 text-start text-sm">
        <div>
          {`${formatNumber(line.cost.totalAmount.amount)} ${
            line.cost.totalAmount.currencyCode
          }`}
        </div>
        {surgeFeeToDisplay.cost && surgeFeeToDisplay.currencyCode && (
          <div className="text-red-700">
            {`(+${formatNumber(surgeFeeToDisplay.cost)} ${
              surgeFeeToDisplay.currencyCode
            })`}
          </div>
        )}
      </div>
      <div className="flex flex-col items-start gap-1 pb-4">
        <div className="text-sm">Booking Date</div>
        <div className="flex w-full items-center justify-between">
          <div>
            {(
              (bookingModalConfirmation?.productLine ?? line)
                .attributes as AttributeInput[]
            ).find(({key}) => key === 'Date & Time')?.value ?? '-'}
          </div>
          <button
            onClick={() => {
              setIsOpenMenu(false);
              setIsBookingModalOpen(true);
            }}
            type="button"
            className="text-sm text-red-400"
          >
            Edit
          </button>
        </div>
      </div>
      <div className="mb-4 flex flex-col items-start gap-1">
        {selectedOptions.map((option) => (
          <Fragment key={option.name}>
            {option.value !== '-' && (
              <div className="flex w-full items-center justify-between">
                <div>{option.value}</div>
                {option.name.includes('Add-On') && option.value !== '-' && (
                  <button
                    onClick={() => handleRemoveOption({...option, value: '-'})}
                    type="button"
                    className="text-sm text-red-400"
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </Fragment>
        ))}
      </div>
      {/* {selectedOptions?.map((v) => (
        <Fragment key={v.name}>
          {v?.name?.includes('Add-On') && v.value === '-' && (
            <Fragment>
              <div className="flex flex-col items-start gap-1 pt-2">
                <Listbox
                  value={selectedOptions.find((e) => e.name === v.name)?.value}
                  onChange={(e) => handleSelectOption({name: v.name, value: e})}
                >
                  {({open}) => (
                    <>
                      <Listbox.Label className="block text-sm font-medium text-gray-700">
                        {v.name}
                      </Listbox.Label>
                      <div className="relative mt-1 w-full">
                        <Listbox.Button className="relative flex w-full cursor-pointer border border-gray-300 py-3 pl-3 pr-10 text-left shadow-sm outline-none ring-0 sm:text-sm">
                          <span className="block truncate text-gray-700">
                            {selectedOptions.find((e) => e.name === v.name)
                              ?.value === '-'
                              ? 'Select refresher'
                              : selectedOptions.find((e) => e.name === v.name)
                                  ?.value}
                          </span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronDownIcon
                              className="h-5 w-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </span>
                        </Listbox.Button>

                        <Transition
                          show={open}
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {options
                              ?.find((e) => e?.name == v.name)
                              ?.values?.map((e) => (
                                <Listbox.Option
                                  key={e}
                                  className={({active}) =>
                                    clsx(
                                      active
                                        ? 'bg-black text-white'
                                        : 'text-gray-900',
                                      'relative cursor-pointer select-none py-2 pl-3 pr-9',
                                    )
                                  }
                                  value={e}
                                >
                                  {({selected}) => (
                                    <>
                                      <span
                                        className={clsx(
                                          selected
                                            ? 'font-semibold'
                                            : 'font-normal',
                                          'block truncate text-start',
                                        )}
                                      >
                                        {e === '-' ? 'Select refresher' : e}
                                      </span>
                                    </>
                                  )}
                                </Listbox.Option>
                              ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </>
                  )}
                </Listbox>
              </div>
            </Fragment>
          )}
        </Fragment>
      ))} */}
      <div className="flex items-center gap-4 pt-12">
        {/* <div>
          <div className="flex items-center border border-black">
            <button
              type="button"
              aria-label="Decrease quantity"
              className="h-10 w-10 text-black transition hover:text-acai-900 disabled:cursor-wait"
              onClick={() => setQuantity(quantity - 1)}
            >
              &#8722;
            </button>
            <div className="px-2 text-center">{quantity}</div>
            <button
              type="button"
              aria-label="Increase quantity"
              className="h-10 w-10 text-black transition hover:text-acai-900 disabled:cursor-wait"
              onClick={() => setQuantity(quantity + 1)}
            >
              &#43;
            </button>
          </div>
        </div> */}
        <div className="flex flex-col gap-1">
          <Button
            className="!h-10 !w-full"
            disabled={isUpdatingCart}
            label="Update Selection"
            handleClick={handleUpdate}
          />
          {isUpdatingCart && <div className="text-sm">Updating...</div>}
          {isUpdateCartError && (
            <div className="text-sm text-red-700">Error updating cart</div>
          )}
        </div>
      </div>
    </form>
  );
}
